import React ,{useState} from "react";
import { X } from "lucide-react";
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { useTranslation } from "react-i18next";

const ReorderModal = ({ 
  visible, 
  onClose, 
  onReorder,
  places, 
  currentPlaceId,
  currentPosition
 }) => {
  const [confirmationData, setConfirmationData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  

  const customStyles = {
    overlay: {
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      fontFamily: 'Montserrat, sans-serif',

    },
    content: {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      right: 'auto',
      bottom: 'auto',
      padding: '24px',
      borderRadius: '8px',
      width: '90%',
      maxWidth: '400px',
      border: 'none',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
      fontFamily: 'Montserrat, sans-serif',

    }
  };
  const styles = {
    modal: {
      overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        fontFamily: 'Montserrat, sans-serif',
      },
      content: {
        position: 'relative',
        top: 'auto',
        left: 'auto',
        right: 'auto',
        bottom: 'auto',
        padding: '24px',
        width: '90%',
        maxWidth: '400px',
        border: 'none',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
        fontFamily: 'Montserrat, sans-serif',
        borderRadius: 19,
      }
    },
    modalContainer: {
      position: 'relative'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '1.5rem'
    },
    modalText: {
      marginBottom: '1rem',
      color: '#6b7280'
    },
    modalList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      maxHeight: '50vh',
      overflowY: 'auto'
    },
    modalItem: {
      width: '100%',
      padding: '0.75rem',
      textAlign: 'left',
      border: '1px solid #e5e7eb',
      borderRadius: '0.5rem',
      background: 'white',
      transition: 'all 0.2s',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      border: 'none'
    },
    modalItemHover: {
      borderColor: '#93c5fd',
      backgroundColor: '#f0f9ff'
    },
    modalItemDisabled: {
      backgroundColor: '#f3f4f6',
      cursor: 'not-allowed'
    },
    modalItemCurrent: {
      borderColor: '#EBF8FF',
      backgroundColor: '#EBF8FF'
    },
    modalItemContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      width: '100%'
    },
    positionBadge: {
      width: '2rem',
      height: '2rem',
      borderRadius: '9999px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 500,
      flexShrink: 0
    },
    normalBadge: {
      backgroundColor: '#51C7F2',
      color: 'white'
    },
    depotBadge: {
      backgroundColor: '#CBD5E0',
      color: 'white'
    },
    addressText: {
      margin: 0,
      fontSize: '0.875rem',
      color: '#1f2937',
      textAlign: 'left',
      flexGrow: 1
    },
    depotText: {
      color: '#6b7280'
    },
    depotLabel: {
      fontSize: '0.75rem',
      color: '#6b7280',
      marginTop: '0.25rem'
    },
    currentLabel: {
      backgroundColor: '#51C7F2',
      borderRadius: '9999px',
      padding: '4px 8px',
      fontSize: '0.75rem',
      color: '#fff',
      fontWeight: 500,
      marginLeft: 'auto',
      flexShrink: 0
    },
    confirmationContent: {
      textAlign: 'center',
      padding: '1rem 0'
    },
    confirmationText: {
      marginBottom: '1rem',
      color: '#4B5563',
      lineHeight: 1.5
    },
    confirmationWarning: {
      color: '#F59E0B',
      fontWeight: 500,
      marginBottom: '2rem'
    },
    closeButton: {
      padding: '0.25rem',
      borderRadius: '50%',
      transition: 'background-color 0.2s',
      lineHeight: 0,
      background: 'none',
      border: 'none',
      cursor: 'pointer'
    },
    closeButtonHover: {
      backgroundColor: '#F7FAFC'
    },
    confirmationButtons: {
      width: '100%',
      fontFamily: 'Montserrat',
      paddingLeft: '1rem',
      paddingRight: '1rem',
      marginBottom: '5px',
      marginTop: '1rem',
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
    },
    confirmButton: {
      width: '150px',
      height: '50px',
      background: '#5BBCEA',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      borderRadius: 7,
      marginTop: 5,
      fontFamily: 'Montserrat',
      marginRight: 10,
      fontWeight: 'bold',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    cancelButton: {
      width: '150px',
      height: '50px',
      background: 'red',
      border: 'none',
      cursor: 'pointer',
      color: 'white',
      borderRadius: 7,
      marginTop: 5,
      fontFamily: 'Montserrat',
      fontWeight: 'bold',
    }
  };

  const handlePositionSelect = (newPosition, waypoint) => {
    setConfirmationData({
      currentPosition,
      newPosition,
      waypoint,
      currentWaypoint: places.find(wp => wp.position === currentPosition && wp.adresse.value.place_id === currentPlaceId)
    });
  };

  const confirmReorder = async () => {
    try {
      setIsLoading(true);
      await onReorder(currentPlaceId, confirmationData.newPosition);
      setConfirmationData(null);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelReorder = () => {
    setConfirmationData(null);
  };
  const{t} = useTranslation()
  return (
    <>
    <Modal
      isOpen={visible}
      onRequestClose={onClose}
      style={customStyles}
      ariaHideApp={false}
    >
      <div className="modal-container">
        <div className="modal-header">
          <h2>{t("move_stop_point")}</h2>
          <button 
            onClick={onClose} 
            className="close-button"
            aria-label="Fermer"
          >
            <X size={20} color="#666" />
          </button>
        </div>
        
        <p className="modal-text">{t("select_new_position")}</p>
        
        <div className="modal-list">
          {places
            .sort((a, b) => a.position - b.position)
            .map((item) => {
              const isDepot = item.depot;
              const isCurrent = item._id === currentPlaceId;
              
              return (
                <button
                  key={item._id}
                  className={`modal-item ${isDepot ? 'depot' : ''} ${isCurrent ? 'current' : ''}`}
                  onClick={() => !isDepot && !isCurrent && handlePositionSelect(item.position, item) } 
                  disabled={isDepot || isCurrent}
                  type="button"
                >
                  <div className="modal-item-content">
                    <div className={`position-badge ${isDepot ? 'depot-badge' : 'normal-badge'}`}>
                      {item.position}
                    </div>
                    <div>
                      <p className={`address-text ${isDepot ? 'depot-text' : ''}`}>
                        {item.adresse?.label || t("unknown_address")}
                      </p>
                      {isDepot && <span className="depot-label">{t("drop_off_point")}</span>}
                    </div>
                  </div>
                  {isCurrent && <span className="current-label">{t("current_location")}</span>}
                </button>
              );
            })}
        </div>
      </div>

      <style jsx>{`
        .modal-container {
          position: relative;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          color: #1f2937;
        }

        .close-button {
          padding: 0.25rem;
          border-radius: 50%;
          transition: background-color 0.2s;
          line-height: 0;
        }

        .close-button:hover {
          background-color: #F7FAFC;
        }

        .modal-text {
          margin-bottom: 1rem;
          color: #6b7280;
        }

        .modal-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
          max-height: 50vh;
          overflow-y: auto;
        }

        .modal-item {
          width: 100%;
          padding: 0.75rem;
          text-align: left;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          background: white;
          transition: all 0.2s;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-item:hover:not(:disabled) {
          border-color: #93c5fd;
          background-color: #f0f9ff;
        }

        .modal-item:disabled {
          background-color: #f3f4f6;
          cursor: not-allowed;
        }

        .modal-item.current {
          border-color: #EBF8FF;
          background-color: #EBF8FF;
        }

        .modal-item-content {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .position-badge {
          width: 2rem;
          height: 2rem;
          border-radius: 9999px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 500;
          flex-shrink: 0;
        }

        .normal-badge {
          background-color: #51C7F2;
          color: white;
        }

        .depot-badge {
          background-color: #CBD5E0;
          color: white;
        }

        .address-text {
          margin: 0;
          font-size: 0.875rem;
          color: #1f2937;
        }

        .depot-text {
          color: #6b7280;
        }

        .depot-label {
          font-size: 0.75rem;
          color: #6b7280;
          margin-top: 0.25rem;
        }

       .current-label {
        background-color: #51C7F2;
        border-radius: 9999px;
        padding: 4px 8px;
        font-size: 0.75rem;
        color: #fff;
        font-weight: 500;
        margin-left: 1rem;
        flex-shrink: 0;
       }
        

      `}</style>
    </Modal>
    <Modal
            isOpen={!!confirmationData}
            onRequestClose={cancelReorder}
            style={styles.modal}
            ariaHideApp={false}
          >
            <div style={styles.modalContainer}>
              <div style={{
                width: '20rem',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}>
                <h2 style={{
                  fontWeight: 'bold',
                  fontFamily: 'Montserrat',
                  fontSize: 20,
                  color: '#51C7F2',
                }}>{t("confirm_move")}</h2>
              </div>
              
              <div style={styles.confirmationContent}>
                
                <p style={styles.confirmationText}>
                  {t("move_stop_point_confirmation_msg1")} <strong>{confirmationData?.currentWaypoint?.adresse.label}</strong> 
                  {t("move_stop_point_confirmation_msg2")} <strong>{confirmationData?.currentPosition}</strong> {t("move_stop_point_confirmation_msg3")} <strong>{confirmationData?.newPosition}</strong>.
                </p>
                
                <p style={styles.confirmationWarning}>
                  {t("move_stop_point_confirmation_msg4")}
                </p>
                
                <div style={styles.confirmationButtons}>
                  <button
                    onClick={confirmReorder}
                    disabled={isLoading}
                    style={styles.confirmButton}
                  >
                    {isLoading ? (
                      <div className="loader"></div>
                    ) : (
                      t("confirm")
                    )}
                  </button>
                  <button
                    onClick={cancelReorder}
                    disabled={isLoading}
                    style={styles.cancelButton}
                  >
                    {t("cancel")}
                  </button>
                </div>
              </div>
            </div>
          </Modal>
    </>
  );
};

ReorderModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  currentIndex: PropTypes.number,
  places: PropTypes.array.isRequired,
  currentPlaceId: PropTypes.string
};

export default ReorderModal;